import React, { ReactNode } from 'react';
import styled from 'styled-components';
import EntityInfoPopover from '../EntityInfoPopover';
import { IStyled } from '../../styles/types';
import { theme } from '../../styles/theme';
import { SvgIcon } from '../Icon';

interface IProps extends IStyled {
  label: string;
  onClick?: () => void;
  onClear?: () => void;
  onShowInfo?: () => void;
  children?: ReactNode;
  labelColor?: string;
  backgroundColor?: string;
  isDisabled?: boolean;
  isLatestVersion?: boolean;
  hideClearButton?: boolean;
}

const StyledCloseIcon = styled(SvgIcon)`
  opacity: 0;
`;

const Container = styled.div<{
  $backgroundColor?: string;
  $hasReducedPaddingRight?: boolean;
  $ableToClear: boolean;
}>`
  position: relative;
  display: inline-grid;
  align-items: center;
  grid-auto-flow: column;
  grid-auto-columns: auto;
  column-gap: 2px;
  padding: 2px 4px;
  ${({ $hasReducedPaddingRight }) =>
    $hasReducedPaddingRight && 'padding-right: 2px;'}
  border-radius: 2px;
  background-color: ${({ $backgroundColor }) => $backgroundColor};
  cursor: ${({ $ableToClear }) => ($ableToClear ? 'pointer' : 'default')};

  &:hover ${StyledCloseIcon} {
    opacity: 1;
  }
`;

const Label = styled.div<{
  $labelColor: string;
  $isDisabled?: boolean;
  $isLatestVersion?: boolean;
}>`
  position: relative;
  font-size: 12px;
  font-weight: 600;
  line-height: 18px;
  word-break: break-all;
  overflow: hidden;
  text-overflow: ellipsis;
  color: ${({ $isLatestVersion, $labelColor }) =>
    $isLatestVersion ? $labelColor : theme.colors.secondary600};
  ${({ $isDisabled }) =>
    $isDisabled &&
    `
    text-decoration: line-through;
  `}
`;

export default ({
  label,
  onClick,
  onClear,
  children,
  labelColor = theme.colors.secondary900,
  backgroundColor = theme.colors.blue500,
  isDisabled,
  isLatestVersion,
  /**
   * special case in FieldBinningDisplay.
   * in this case, we don't want to display the remove button, but need to provide click on
   * remove. But handleClick() in this component stopPropagation() already.
   * Therefore we need to pass the onClear to support removing.
   * The case: <BasicEntity><VersionedEntity /></BasicEntity>
   */
  hideClearButton = false,
  className,
  onShowInfo,
}: IProps) => {
  const handleMouseDown = (event: React.SyntheticEvent) => {
    event.preventDefault();
    event.stopPropagation();
  };

  const handleRemove = () => {
    if (onClear) {
      onClear();
    }
  };

  const handleOpenInfo = () => {
    if (onShowInfo) {
      onShowInfo();
    }
  };

  return (
    <Container
      onMouseDown={handleMouseDown}
      onClick={handleRemove}
      $backgroundColor={backgroundColor}
      $hasReducedPaddingRight={!hideClearButton && !!onClear}
      $ableToClear={!!onClear}
      className={className}
    >
      <Label
        onClick={onClick}
        $labelColor={labelColor}
        $isDisabled={isDisabled}
        $isLatestVersion={isLatestVersion}
      >
        {label}
      </Label>
      <EntityInfoPopover
        containerSize={12}
        iconSize={12}
        onOpen={handleOpenInfo}
        closeOnAncestorScroll={true}
      >
        {children}
      </EntityInfoPopover>
      {!hideClearButton && !!onClear && (
        <StyledCloseIcon
          name="icon-x-bold"
          width={8}
          height={8}
          customFill="currentColor"
          isHoverable={true}
        />
      )}
    </Container>
  );
};
